/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import {
	createBrowserHistory,
	matchRoute,
	type BrowserHistory,
	type Route,
	type HistoryBlocker,
	type MatchedRoute,
} from '@atlassian/react-resource-router';
import type {
	DynamicRouteBrowserHistory,
	Location,
	HistoryAction,
} from '@atlassian/react-resource-router/src/common/types';

const LOCATION = 'spa.create-spa-history';

const createCustomHistoryBlock =
	(
		routeBlockFns: HistoryBlocker[],
		currentRoutes: Route[],
		history: BrowserHistory & DynamicRouteBrowserHistory,
	) =>
	(blockFn: HistoryBlocker) => {
		const matchedRoute = matchRoute(
			currentRoutes,
			history.location.pathname,
			history.location.search,
		);
		// This should never be the case as in SPA we have a default route
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		if (!matchedRoute) return () => {};

		if (!routeBlockFns.includes(blockFn)) {
			routeBlockFns.push(blockFn);
		}

		// returns an "unregister" block
		return () => {
			const idx = routeBlockFns.indexOf(blockFn);
			if (idx !== -1) {
				routeBlockFns.splice(idx, 1);
			}
		};
	};

const createSpaHistory = (initialRoutes: Array<Route>) => {
	const routeBlockFns: HistoryBlocker[] = [];
	// oxlint-disable-next-line typescript/consistent-type-assertions
	let blockArgs = [] as unknown as Parameters<HistoryBlocker>;
	let nextMatchedRoute: MatchedRoute | null;
	const currentRoutes: Route[] = initialRoutes;

	const history: BrowserHistory & DynamicRouteBrowserHistory = createBrowserHistory({
		/* We abuse this api to allow us to run as many blockers as we want, allowing the transition
		 * to resume only when all return true or bail out as soon as the first one returns false */
		getUserConfirmation: (__, done) => {
			const matchedRoute = matchRoute(
				currentRoutes,
				history.location.pathname,
				history.location.search,
			);

			// This should never be the case as in SPA we have a default route
			if (!matchedRoute || !nextMatchedRoute) {
				return done(true);
			}

			// we execute the blockers in reverse order, so the last registered is the first to run
			// They are executed synchronously until one returns false or a promise
			// If a promise is returned we execute all remaining blockers as chained promises
			let sync = true;
			for (let i = routeBlockFns.length - 1; i >= 0; i -= 1) {
				const result = routeBlockFns[i](...blockArgs);
				if (result instanceof Promise) {
					sync = false;
					routeBlockFns
						.slice(0, i + 1)
						.reverse()
						.reduce(
							// eslint-disable-next-line @typescript-eslint/no-loop-func
							(promise, blocker) =>
								promise.then((res) =>
									res === false ? Promise.resolve(false) : blocker(...blockArgs),
								),
							Promise.resolve(result),
						)
						.then((res) => {
							routeBlockFns.length = 0;
							done(res);
						})
						.catch((err) => {
							log.safeErrorWithoutCustomerData(LOCATION, 'Error with getUserConfirmation', err);
							done(true);
						});
					break;
				}
				if (result === false) {
					done(false);
					return;
				}
			}
			if (sync) {
				routeBlockFns.length = 0;
				done(true);
			}
		},
	});

	// For lazy loaded route maps we need to keep track of the current routes here
	// as they will be changing. When we "match" for the route blocking code we need
	// to ensure we match against the current routes, not the routes at the time the function
	// was created.
	history.refreshRoutes = (newRoutes: Route[]): void => {
		// Replace currentRoutes in place as it's captured in a closure above..
		currentRoutes.length = 0;
		currentRoutes.push(...newRoutes);
	};

	/* We set the main "blocker", that is also used enable next location access in getUserConfirmation
	 * (going around getUserConfirmation API limitations) */
	history.block((location: Location, action: HistoryAction) => {
		blockArgs = [location, action];
		nextMatchedRoute = matchRoute(currentRoutes, location.pathname, location.search);
		// Must return a string or getUserConfirmation is not called
		return '';
	});
	// @ts-expect-error - we're using a custom blocker
	history.block = createCustomHistoryBlock(routeBlockFns, currentRoutes, history);
	return history;
};

/**
 * Monkey patches the push/replace methods of the react router history object to
 * perform a full page refresh instead of history push/replace when a route is not valid
 * (i.e. legacy pages). This is required to fix the browser back button not working correctly
 * after redirecting to a legacy page.
 * JSPA-900
 */

const createSpaHistory1 = (routes: Route[]): BrowserHistory => createSpaHistory(routes);

export default createSpaHistory1;
